import { useEffect, RefObject } from 'react';

export type UnionDragEvent = React.DragEvent<HTMLElement> | DragEvent;

interface UseNativeDropZoneParams {
  rowRef: RefObject<HTMLTableRowElement>;
  onDragOver: (event: UnionDragEvent, index: number, rowElement?: HTMLElement) => void;
  onDragLeave: (event: UnionDragEvent, rowElement?: HTMLElement) => void;
  onDrop: (event: UnionDragEvent) => void;
  isReorderingMode: boolean | undefined;
  index: number;
  type: string;
}

/**
 * A custom hook that bypasses UI library restrictions by attaching native
 * HTML5 drag-and-drop event listeners directly to a table row DOM node.
 */
export const useNativeDropZone = ({
  rowRef,
  onDragOver,
  onDragLeave,
  onDrop,
  isReorderingMode,
  index,
  type
}: UseNativeDropZoneParams) => {
  useEffect(() => {
    const rowNode = rowRef.current;

    if (!rowNode || !isReorderingMode) {
      return;
    }

    const handleDragOver = (e: DragEvent) => onDragOver(e, index, rowNode);
    const handleDragLeave = (e: DragEvent) => onDragLeave(e, rowNode);
    const handleDrop = (e: DragEvent) => onDrop(e);

    rowNode.addEventListener('dragover', handleDragOver);
    rowNode.addEventListener('dragleave', handleDragLeave);
    rowNode.addEventListener('drop', handleDrop);

    return () => {
      rowNode.removeEventListener('dragover', handleDragOver);
      rowNode.removeEventListener('dragleave', handleDragLeave);
      rowNode.removeEventListener('drop', handleDrop);
    };
  }, [rowRef, onDragOver, onDragLeave, onDrop, isReorderingMode, index, type]);
};
